/* eslint-disable no-shadow */
/* eslint-disable no-param-reassign */
import _ from "lodash";
import { useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { sha256 } from "js-sha256";
import { callApi } from "../../api";
import { useAuth } from "../useAuth";
import { tzformat } from "../../utility/dataFormat";
import { getFirstName, toTitleCase } from "../../utility/Utils";

const LOADING_TYPES = {
  FETCH_SUMMARY: "fetch_summary",
  FETCH_TRANSACTIONS: "fetch_transactions",
  DELETE: "delete",
  FETCH_HISTORY: "fetch_history",
  FETCH_LOYALTY: "fetch_loyalty",
};

const PAYMENT_PROVIDERS = ["stripe", "nmi"];

const useDailyReports = () => {
  const { selectedBusiness, selectedBusinessDetail } = useAuth();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [paymentTransactions, setPaymentTransactions] = useState(null);
  const [byPassTransactions, setByPassTransactions] = useState(null);
  const [history, setHistory] = useState(null);
  const paymentProvider = selectedBusinessDetail?.info?.webpaymentProvider;

  const getSummary = async (startDate, endDate) => {
    setLoading(LOADING_TYPES.FETCH_SUMMARY);
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/queue/summary?start=${startDate}&end=${endDate}`
    );
    if (res && res.status === true) {
      setSummary(res.data);
    } else {
      toast.error((res && res.message) || "Failed to fetch sales summary!");
    }
    setLoading(null);
  };

  const getProviderTransactions = async (date) => {
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/${paymentProvider}/transactions/${date}`
    );
    if (res && res.status === true) {
      setPaymentTransactions(res.data);
    } else {
      toast.error(res?.message || "Failed to fetch payment ransactions!");
    }
  };

  const getByPassTransactions = async (date) => {
    const code = selectedBusiness?.info?.code;
    if (!code) return;
    const pinhash = sha256(code);
    const env = process.env.REACT_APP_ENV === "production" ? "prod" : "test";
    // const env = "prod";
    // const pinhash =
    // "920a6421f4b7573cfb0d0fb8d61252665275d0b97bc18683638dee3e8a015f01";
    const res = await callApi(
      `/protected/business/${
        selectedBusiness.id
      }/iorequest?dest=${encodeURIComponent(
        `:BID:/transactions/bypassmode/${date}?env=${env}&pinhash=${pinhash}`
      )}`
    );
    if (res?.status) {
      setByPassTransactions(res.data);
    } else {
      setByPassTransactions(null);
    }
  };

  const getTransactions = async (date) => {
    setLoading(LOADING_TYPES.FETCH_TRANSACTIONS);
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/transactions/${date}`
    );
    if (res && res.status === true) {
      setTransactions(res.data);
      if (paymentProvider) await getProviderTransactions(date);
      await getByPassTransactions(date);
    } else {
      toast.error((res && res.message) || "Failed to fetch transactions!");
    }
    setLoading(null);
  };

  const getCustomerName = (qitem) => {
    let name;
    let customername;
    const isGuest = qitem.phones && qitem.phones.type === "TEMP";
    let profile;
    if (isGuest) {
      if (qitem && qitem.checkoutdetails && qitem.checkoutdetails.length) {
        customername = _.find(qitem.checkoutdetails, {
          name: "customername",
        });
        if (customername) {
          name = customername.value;
        }
      }
    }
    if (!name) {
      if (
        qitem.phones.customerprofiles &&
        qitem.phones.customerprofiles.length
      ) {
        profile = qitem.phones?.customerprofiles[0];
        const fn = (profile?.firstname?.length && profile?.firstname) || null;
        const ln = (profile?.lastname?.length && profile?.lastname) || null;

        if (fn && ln) {
          name = `${fn} ${ln}`.trim();
        } else if (fn) {
          name = `${fn}`.trim();
        } else if (ln) {
          name = `${ln}`.trim();
        } else {
          name = "Unnamed Customer";
        }
        // name = `${profile.firstname} ${profile.lastname}`.trim();
      } else {
        name = "Unnamed Customer";
      }
    }
    return name;
  };

  const getCustomerEmail = (qitem) => {
    const isGuest = qitem.phones && qitem.phones.type === "TEMP";
    let profile;
    let email;
    if (
      !isGuest &&
      qitem.phones.customerprofiles &&
      qitem.phones.customerprofiles.length
    ) {
      profile = _.first(
        qitem.phones.customerprofiles,
        (p) => p.email && p.email.length > 2
      );
      email = (profile && profile.email) || "";
    }

    return email;
  };

  const dbFormat = "YYYY-MM-DD HH:mm:ss";

  const getRemoveDetail = (qitem, employees) => {
    if (qitem && qitem.checkoutdetails && qitem.checkoutdetails.length) {
      const removedetail = _.find(qitem.checkoutdetails, {
        name: "removedetail",
      });
      const detailObj = removedetail && JSON.parse(removedetail.value);
      const removedAt =
        removedetail &&
        tzformat(removedetail.created_at, "UTC", dbFormat, "hh:mm a");
      const removedBy = _.find(qitem.checkoutdetails, { name: "removed_by" });
      const e = removedBy && _.find(employees, { id: +removedBy.value });
      const emp =
        (e && e.full_name) ||
        (detailObj && detailObj.staff && detailObj.staff.name) ||
        "N/A";
      return {
        by: emp,
        at: removedAt,
        kb: detailObj && detailObj.kb,
        desktop: detailObj && detailObj.desktop,
      };
    }
    return "";
  };

  const getTaxForService = (sl) => {
    let tax;
    const taxrate = selectedBusiness?.info.taxrate / 100;
    if (sl.service) {
      if (sl.service.attributes && sl.service.attributes.taxable) {
        tax = (sl.price - sl.discount) * sl.quantity * taxrate;
      } else {
        tax = 0;
      }
    } else if (sl.product) {
      if (sl.product.attributes && sl.product.attributes.taxable === false) {
        tax = 0;
      } else {
        tax = (sl.price - sl.discount) * sl.quantity * taxrate;
      }
    }
    return tax || 0;
  };

  const getCategoryFromService = (serviceOrProduct, isService) => {
    let cat;
    if (isService) {
      cat = _.find(selectedBusiness.info.servicecategories, {
        id: serviceOrProduct?.category_id,
      });
    } else {
      cat = _.find(selectedBusiness.info.productcategories, {
        id: serviceOrProduct?.category_id,
      });
    }
    return cat && cat.name;
  };

  const PRETTY_STATUS = {
    CHECKOUT: "Completed",
    SERVICE_NOCHECKOUT: "Deleted after service",
    CHECKIN_NOSERVICE: "Deleted before service",
    ACTIVE: "In Service",
    PAYMENTREADY: "In Service",
    CHECKOUTREADY: "In Service",
    CHECKIN: "In queue",
    ASSIGNED: "In queue",
    DELETED: "Deleted",
  };

  const getStatusPretty = (st) => PRETTY_STATUS[st] || st;

  const getDetail = (allTx, employees) => {
    const saleslogDetails = [];
    const detail = {
      all: [],
      coupons: [],
    };
    const coupons = [];
    let unmatched;

    const getTipDistribution = (item) => {
      const dist = item.payee ? item.payeetips : item.paidtips;
      const payee = item.payee
        ? _.find(allTx.transactions, { id: item.payee })
        : item;
      const allTxInGroup = payee
        ? _.filter(
            allTx.transactions,
            (tx) => tx.id === payee.id || tx.payee === payee.id
          )
        : [item];
      const totalTip =
        _.sumBy(
          allTxInGroup,
          (g) => (g.checkout && g.checkout.tipamount) || 0
        ) || 0;

      item.transactionGroups = _.map(allTxInGroup, (tx) => {
        const customer =
          tx.phones &&
          tx.phones.customerprofiles &&
          tx.phones.customerprofiles.length &&
          tx.phones.customerprofiles[0];
        const name =
          (customer && toTitleCase(customer.firstname || customer.lastname)) ||
          `INV${tx.id}`;
        return {
          id: tx.id,
          name,
        };
      });

      if ((dist && dist.length) || totalTip) {
        item.distribution = _.map(dist, (dd) => {
          const e =
            dd.employee_id &&
            _.find(selectedBusiness.employees, { id: dd.employee_id });
          const oe =
            dd.orig_employee_id &&
            _.find(selectedBusiness.employees, { id: dd.orig_employee_id });
          const d = _.extend({}, dd);
          const tx =
            dd.queue_id &&
            dd.queue_id !== item.id &&
            _.find(allTx.transactions || [], { id: dd.queue_id });
          let customer;
          d.self = 0;
          d.changed = 0;
          d.added = 0;
          if (d.queue_id === item.id) {
            d.self = 1;
          }
          if (e) {
            d.staff = toTitleCase(getFirstName(e.nickname || e.full_name));
          }
          if (oe) {
            d.origStaff = toTitleCase(
              getFirstName(oe.nickname || oe.full_name)
            );
          }
          if (
            (d.orig_employee_id || d.orig_tipamount) &&
            (d.orig_employee_id !== d.employee_id ||
              d.orig_tipamount !== d.tipamount)
          ) {
            d.changed = 1;
          }
          if (d.orig_employee_id === 0) {
            d.added = 1;
          }
          const gotTx = d.self ? item : tx;
          if (gotTx) {
            customer =
              gotTx &&
              gotTx.phones &&
              gotTx.phones.customerprofiles &&
              gotTx.phones.customerprofiles.length &&
              gotTx.phones.customerprofiles[0];
            d.customerName =
              customer && toTitleCase(customer.firstname || customer.lastname);
          }
          return d;
        });
        item.mydistribution = _.filter(item.distribution, (d) => d.self);

        item.transactionGroups = _.map(allTxInGroup, (tx) => {
          const customer =
            tx.phones &&
            tx.phones.customerprofiles &&
            tx.phones.customerprofiles.length &&
            tx.phones.customerprofiles[0];
          const name =
            (customer &&
              toTitleCase(customer.firstname || customer.lastname)) ||
            `INV${tx.id}`;
          return {
            id: tx.id,
            name,
          };
        });
        item.totalTip = totalTip;
      }
    };

    const updateCheckout = (item) => {
      const staffFromItems = [];
      let checkoutServices;
      let addedServices;
      let allStaffIds;
      let allStaff;
      let staffNames;
      let keys;
      const eid =
        item.status === "CHECKIN_NOSERVICE"
          ? item.booked_for
          : item.serviced_by;
      let staff = _.find(employees, {
        id: eid,
      }) || {
        name: "Bot",
      };
      const phone = item.phones && item.phones.phone;
      let staffName = staff ? staff.full_name : "N/A";
      const name = getCustomerName(item);
      const getStaffFromSaleslogs = (staffIds) =>
        _.filter(employees, (e) => _.includes(staffIds, e.id));
      const checkoutItem = {};

      if (
        item.status === "CHECKIN_NOSERVICE" ||
        item.status === "SERVICE_NOCHECKOUT"
      ) {
        item.removeDetail = getRemoveDetail(item, employees);
      }
      item.phone = phone;
      item.serviceDur = item.service_at
        ? moment(item.updated_at).diff(moment(item.service_at), "minutes")
        : 0;
      item.waitDur = moment(item.service_at || item.updated_at).diff(
        moment(item.checkin_at),
        "minutes"
      );

      item.customer = {
        id: item.phones.id,
        type: item.phones.type,
        isTemp: item.phones.type === "TEMP",
        isGuest: item.phones.type === "GUEST",
        isRepeat: item.phones.type !== "TEMP" && item.phones.pastcheckout,
        isNew: item.phones.type !== "TEMP" && !item.phones.pastcheckout,
        phone: item.phones.phone,
        email: getCustomerEmail(item),
        name,
      };
      checkoutItem.customer = {
        name: item.customer.name,
        phone: item.customer.phone,
        type: item.customer.type,
      };

      if (item.checkout) {
        if(item?.checkout?.created_at) {
          item.checkout.checkoutTime = moment.utc(item?.checkout?.created_at).tz(selectedBusiness?.business?.timezone || "America/Los_Angeles").format("hh:mm a")
        } else {
          item.checkout.checkoutTime = "No Date"
        }
        // item.checkout.checkoutTime = tzformat(
        //   item.checkout.created_at,
        //   selectedBusiness?.business?.timezone || "America/Los_Angeles",
        //   dbFormat,
        //   "hh:mm a"
        // );

        checkoutItem.time = item.checkout.checkoutTime;
        checkoutItem.checkout = { ...(item.checkout || {}) };
        checkoutItem.tender = "";
        if (item.checkout.cashamount) {
          checkoutItem.tender += "CASH";
        }
        if (item.checkout.creditamount) {
          if (checkoutItem.tender) {
            checkoutItem.tender += "+";
          }
          checkoutItem.tender += "CREDIT";
        }
        if (item.checkout.giftamount) {
          if (checkoutItem.tender) {
            checkoutItem.tender += "+";
          }
          checkoutItem.tender += "GIFT";
        }
        if (!checkoutItem.tender) {
          checkoutItem.tender = "N/A";
        }

        if (item.checkout.saleslogs && item.checkout.saleslogs.length) {
          _.each(item.checkout.saleslogs, (sl) => {
            staffFromItems.push(sl.employees);
            sl.tax = getTaxForService(sl);
            sl.total = sl.price * sl.quantity - sl.discount + sl.tax;
            const gotStaff = getStaffFromSaleslogs(sl.employees);
            const gotStaffNames = _.map(gotStaff, "full_name").join(", ");
            const slItem = _.extend(
              {
                id: sl.id,
                sl: { ...(sl || {}) },
                staff: gotStaffNames,
                sp: sl.product ? "Product" : "Service",
                service: sl.service ? sl.service.name : sl.product?.name,
                category: getCategoryFromService(
                  sl.service || sl.product,
                  !!sl.service
                ),
              },
              checkoutItem
            );

            saleslogDetails.push(slItem);
          });
        } else {
          saleslogDetails.push(
            _.extend(
              {
                id: item.checkout.id,
                sl: null,
                staff: staffName || "N/A",
                sp: "Service",
                service: item.checkout.isRefund ? "Refund" : "Service Missing",
                category: "N/A",
              },
              checkoutItem
            )
          );
        }
        allStaffIds = _.compact(_.uniq(_.flatten(staffFromItems)));
        allStaff = getStaffFromSaleslogs(allStaffIds);
        staffNames = [];
        checkoutServices =
          (item.checkout.saleslogs &&
            _.compact(_.map(item.checkout.saleslogs, "service_id"))) ||
          [];
        addedServices = _.map(
          _.filter(
            item.services,
            (serv) =>
              serv.pivot &&
              !serv.pivot.at_checkin &&
              _.includes(checkoutServices, serv.id)
          ),
          "id"
        );

        if (item.checkout.employee_id || allStaff.length) {
          if (allStaff.length) {
            _.each(allStaff, (item) => {
              staffNames.push(item.name || item.full_name);
            });
            staffName = staffNames.join(",*");
          } else {
            staff = _.find(selectedBusiness.employees, {
              id: item.checkout.employee_id,
            });
            staffName = staff ? staff.name || staff.full_name : "N/A";
          }
        }
        item.serviceDur = moment(item.checkout_at).diff(
          moment(item.service_at),
          "minutes"
        );
        item.waitDur = moment(item.service_at).diff(
          moment(item.checkin_at),
          "minutes"
        );

        item.checkout.removedServices = _.filter(
          item.services,
          (serv) =>
            serv.pivot &&
            serv.pivot.at_checkin &&
            !_.includes(checkoutServices, serv.id)
        );

        _.each(
          _.filter(
            item.checkout.saleslogs,
            (sl) => sl.service_id && _.includes(addedServices, sl.service_id)
          ),
          (sl) => {
            sl.upsale = true;
          }
        );

        if (item.checkout.feesamount && item.checkout.withfees) {
          item.checkout.feesdetail = JSON.parse(item.checkout.withfees);
        }

        if (item.checkout.coupons) {
          keys = item.checkout.withmultiplecoupons
            ? _.keys(JSON.parse(item.checkout.withmultiplecoupons))
            : [];
          _.each(item.checkout.coupons, (coupon) => {
            if (_.includes(keys, String(coupon.id))) {
              coupons.push(coupon);
              coupon.txid = item.id;
            }
          });
        }
        if (item.checkout.globalcoupons) {
          _.each(item.checkout.globalcoupons, (coupon) => {
            coupons.push(coupon);
          });
        }
        item.payment = item.payments.length ? _.maxBy(item.payments, "id") : {};
        item.payment.multiple =
          item.payments &&
          item.payments.length &&
          _.compact(_.uniq(_.map(item.payments, "auth_code"))).length > 1;
        // Remove VOID if there is a corresponding CAPTURE
        _.each(_.filter(item.payments, { charge_status: "CAPTURE" }), (p) => {
          _.remove(
            item.payments,
            (q) => q.queue_id === p.queue_id && q.charge_status === "VOID"
          );
        });
      }

      if (item.deletedBefore || item.deletedAfter) {
        item.totalAmount = _.sumBy(item.services, (s) => s.price || 0) || 0;
        item.totalAmount +=
          _.sumBy(
            item.products,
            (p) => p.pivot.product_quantity * p.price || 0
          ) || 0;

        item.checkinTime = tzformat(
          item.checkin_at,
          "UTC",
          dbFormat,
          "hh:mm a"
        );
        item.deleteTime = tzformat(item.updated_at, "UTC", dbFormat, "hh:mm a");
      }

      if (staffName) {
        item.staff = `*${staffName}`;
      }

      getTipDistribution(item);
      return item;
    };

    _.each(allTx?.transactions, (tx) => {
      const apptNotes = _.find(
        tx.checkoutdetails,
        (d) => d.name === "appointmentnotes"
      );
      tx.deletedBefore = tx.status === "CHECKIN_NOSERVICE";
      tx.deletedAfter = tx.status === "SERVICE_NOCHECKOUT";
      tx.active = _.includes(
        ["SERVICE", "PAYMENTREADY", "CHECKOUTREADY"],
        tx.status
      );
      tx.waiting = _.includes(["CHECKIN", "ASSIGNED"], tx.status);
      tx.success = tx.status === "CHECKOUT" && tx.checkout;
      tx.appointmentNotes =
        apptNotes && apptNotes.value && JSON.parse(apptNotes.value);
      if (
        tx.status !== "DELETED" &&
        (tx.checkout || tx.status !== "CHECKOUT")
      ) {
        detail.all.push(updateCheckout(tx));
      }
    });

    _.each(detail.all, (tx) => {
      let payee;
      if (tx.checkout && tx.checkout.payee) {
        payee = _.find(detail.all, { id: tx.checkout.payee });
        if (payee) {
          tx.payeeName = payee && payee.customer && payee.customer.name;
        }
      }
      tx.statusPretty = getStatusPretty(tx.status);
      tx.isCash =
        tx?.checkout?.cashamount !== 0 &&
        tx?.checkout?.creditamount === 0 &&
        tx?.checkout?.giftamount === 0;
      tx.isCredit =
        tx?.checkout?.cashamount === 0 &&
        tx?.checkout?.creditamount !== 0 &&
        tx?.checkout?.giftamount === 0;
      tx.isGift =
        tx?.checkout?.cashamount === 0 &&
        tx?.checkout?.creditamount === 0 &&
        tx?.checkout?.giftamount !== 0;
      tx.isRefund = tx?.checkout?.isRefund;
    });
    const couponGroups = _.groupBy(coupons, (item) =>
      item.sms_id
        ? `TEXT: ${item.smses.campaigns.code_name}`
        : item.campaigns
        ? `STORE: ${item.campaigns.global_coupon}`
        : false
    );
    // console.log({ couponGroups });

    _.each(couponGroups, (v, k) => {
      detail.coupons.push({
        title: k,
        count: _.isArray(v) ? v.length : 1,
        amount: _.isArray(v)
          ? _.sumBy(v, "discount_amount")
          : (v && v.discount_amount) || 0,
      });
    });

    const updateTxDetail = {
      stripe: (paymentTx) => {
        const hasTx = detail && detail.all && detail.all.length;
        const _unmatched = [];
        if (paymentTx && paymentTx.length) {
          _.each(paymentTx, (item) => {
            const d = item.created && moment(item.created * 1000).tz(selectedBusiness?.business?.timezone || "America/Los_Angeles")
            const pm =
              item.payment_method_details ||
              (item.charge && item.charge.payment_method_details);
            const brand = pm && pm.card && pm.card.brand;
            const last4 = pm && pm.card && pm.card.last4;
            const reco = {
              id: item.id,
              status: item.status,
              success: item.status && item.status === "succeeded",
              date: d && d.format("MMM DD, YYYY hh:mmA"),
              type: item.object,
              isRefund: item.object === "refund",
              settled:
                item.balance_transaction &&
                item.balance_transaction.status === "available",
              amount: item.amount,
              batch_id: null,
              mappedCard: brand || "Card",
              last4: last4 || "XXXX",
              receiptUrl:
                item.receipt_url || (item.charge && item.charge.receipt_url),
              provider: "STRIPEWEBORDER",
              paymentProvider: "stripe",
              failure_code: item.failure_code,
              failure_message: item.failure_message,
              customer: item.customer,
            };
            const exists =
              hasTx &&
              _.find(
                detail.all,
                (tx) =>
                  tx.payment && reco.id && tx.payment.auth_guid === reco.id
              );
            if (exists) {
              if (
                (!exists.payment?.mappedCard ||
                  exists.payment?.mappedCard === "UNKNOWN") &&
                reco.mappedCard
              ) {
                exists.payment.mappedCard = reco.mappedCard;
              }
              exists.reconciliation = reco;
              exists.creditAmountUnmatchedWithProvider =
                exists.checkout.creditamount !== item.amount;
            } else {
              _unmatched.push(reco);
            }
          });
        }
        if (hasTx) {
          _.each(detail.all, (tx) => {
            if (
              tx.checkout &&
              tx.checkout.creditamount &&
              tx.creditAmountUnmatchedWithProvider
            ) {
              tx.reconciliation = { unmatched: true };
            }
          });
        }
        unmatched = _unmatched;
      },
      nmi: (allTx) => {
        const paymentTx = _.filter(
          allTx,
          (tx) =>
            tx !== "\n" &&
            tx &&
            tx.action &&
            tx.action.action_type !== "validate"
        );
        const hasTx = detail && detail.all && detail.all.length;
        const _unmatched = [];
        if (paymentTx && paymentTx.length) {
          _.each(paymentTx, (item) => {
            const d =
              item.action &&
              item.action.date &&
              moment.utc(item.action.date, "YYYYMMDDHHmmss").tz(selectedBusiness?.business?.timezone || "America/Los_Angeles");
            const type = item.action && item.action.action_type;
            const reco = {
              id: item.transaction_id,
              status: item.condition,
              success: item.action && item.action.success === "1",
              date: d && d.format("MMM DD, YYYY hh:mmA"),
              type,
              isRefund:
                type === "refund" || type === "void" || type === "return",
              settled:
                item.condition === "settled" || item.condition === "complete",
              amount:
                item.action && item.action.amount && Math.round(item.action.amount * 100),
              batch_id: item.action && item.action.batch_id,
              mappedCard: item.cc_type || "Card",
              last4: (item.cc_number && item.cc_number.substr(-4)) || "XXXX",
              receiptUrl: `${`/growthzilla/remote/public/protected/business/${selectedBusiness.id}`}/nmireceipt/${
                item.transaction_id
              }`,
              provider: "NMIWEBORDER",
              paymentProvider: "nmi",
              failure_code: item.failure_code,
              failure_message: item.failure_message,
              customer: item.customer,
            };
            const exists =
              hasTx &&
              _.find(
                detail.all,
                (tx) =>
                  tx.payment && reco.id && tx.payment.auth_guid === reco.id
              );
            if (exists) {
              if (
                (!exists.payment.mappedCard ||
                  exists.payment.mappedCard === "UNKNOWN") &&
                reco.mappedCard
              ) {
                exists.payment.mappedCard = reco.mappedCard;
              }
              exists.reconciliation = reco;
              exists.creditAmountUnmatchedWithProvider =
                exists.checkout.creditamount !== (item.action && item.action.amount &&  Math.round(item.action.amount * 100));
            } else {
              _unmatched.push(reco);
            }
          });
        }
        if (hasTx) {
          _.each(detail.all, (tx) => {
            if (
              tx.checkout &&
              tx.checkout.creditamount &&
              tx.creditAmountUnmatchedWithProvider
              // (!tx.reconciliation || !tx.reconciliation.id)
            ) {
              tx.reconciliation = { unmatched: true };
            }
          });
        }
        unmatched = _unmatched;
      },
    };

    if (paymentProvider && PAYMENT_PROVIDERS?.includes(paymentProvider)) {
      updateTxDetail[paymentProvider](paymentTransactions);
    }

    return {
      ...detail,
      saleslogDetails,
      unmatched,
    };
  };

  const deleteTransaction = async ({ txId, reason, note }) => {
    setLoading(LOADING_TYPES.DELETE);
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/removetransaction/${txId}`,
      "POST",
      {
        reason,
        note,
        src: "Uzeli Dashboard",
      }
    );
    if (res?.status) {
      // uodate cache
      setTransactions({
        ...transactions,
        transactions: _.filter(
          transactions?.transactions || [],
          (t) => t.id !== txId
        ),
      });

      toast.success("Transaction deleted successfully");
    } else {
      toast.error(res?.message || "Failed to remove transaction");
    }
    setLoading(null);
  };

  const checkPaymentStatus = (q) => {
    if (q.payment && q.payment.auth_resp && q.payment.auth_code) {
      return true;
    }
    const logs = q.paymentlogs || [];
    const hasLogs = logs && logs.length;
    const isDejavooSuccess =
      hasLogs &&
      !!_.find(
        logs,
        (l) =>
          l.data &&
          typeof l.data === "string" &&
          l.data.indexOf("<xmp>") > -1 &&
          (l.datatype === "response" || l.datatype === "statusresponse") &&
          (l.data.indexOf("<ResultCode>0</ResultCode>") > -1 ||
            l.data.indexOf("<ResultCode>00</ResultCode>") > -1)
      );
    const isPaxSuccess =
      isDejavooSuccess ||
      (hasLogs &&
        !!_.find(
          logs,
          (l) =>
            l.mappedData &&
            l.isJson &&
            Array.isArray(l.mappedData) &&
            (l.datatype === "response" || l.datatype === "statusresponse") &&
            l.mappedData.length > 4 &&
            l.mappedData[0] === null &&
            parseInt(l.mappedData[4], 10) === 0
        ));
    const isStripeSuccess =
      isPaxSuccess ||
      (hasLogs &&
        !!_.find(
          logs,
          (l) =>
            l.mappedData &&
            l.isJson &&
            (l.datatype === "response" ||
              l.datatype === "statusresponse" ||
              l.datatype === "process_token_response") &&
            l.mappedData.data &&
            l.mappedData.data.auth_resp === "00"
        ));
    const isPaxSmartSuccess =
      isStripeSuccess ||
      (hasLogs &&
        !!_.find(
          logs,
          (l) =>
            l.mappedData &&
            l.isJson &&
            (l.datatype === "response" || l.datatype === "statusresponse") &&
            l.mappedData &&
            l.mappedData.authCode &&
            l.mappedData.respCode
        ));
    const isNmiSuccess =
      isPaxSmartSuccess ||
      (hasLogs &&
        !!_.find(
          logs,
          (l) =>
            l.mappedData &&
            l.isJson &&
            (l.datatype === "response" ||
              l.datatype === "statusresponse" ||
              l.datatype === "process_token_response") &&
            l.mappedData.data &&
            l.mappedData.data.response &&
            l.mappedData.data.authcode
        ));

    return isNmiSuccess || "Unknown";
  };

  const getTransactionLog = (d) => {
    if (!d) {
      return {};
    }
    const dStr = d && d.toString() === "[object Object]";
    if (dStr) {
      _.each(d, (v, k) => {
        const isString = typeof v === "string";
        const isArr =
          isString &&
          v &&
          v.indexOf('["') === 0 &&
          v.split("").reverse().join("").indexOf("]") === 0;
        const isJson =
          isString &&
          v &&
          ((v.indexOf("{") === 0 &&
            v.split("").reverse().join("").indexOf("}") === 0) ||
            isArr);
        // const isReceipt = isString && v && v.indexOf("~~~~") === 0;
        let parsedJson = v;
        if (isJson) {
          try {
            parsedJson = JSON.parse(v);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
        const isObject =
          parsedJson && parsedJson.toString() === "[object Object]";
        d[k] = Array.isArray(parsedJson)
          ? _.map(parsedJson, getTransactionLog)
          : isObject
          ? getTransactionLog(parsedJson)
          : parsedJson;
      });
    }
    return d;
  };

  const getLogStats = (logs) => {
    const s = {};
    _.each(logs, (l) => {
      if (s[l.datatype]) {
        s[l.datatype] += 1;
      } else {
        s[l.datatype] = 1;
      }
    });
    return _.map(s, (v, k) => ({
      name: k,
      value: v,
    }));
  };

  const getHistory = async (opt, tx) => {
    setLoading(LOADING_TYPES.FETCH_HISTORY);
    const res = await callApi(
      `/protected/admin/${selectedBusiness.id}/search/${opt}/${tx}`
    );
    if (res?.status) {
      const d = res.data;
      if (d && d.paymentlogs && d.paymentlogs.length) {
        _.each(d.paymentlogs, (l) => {
          if (l.data) {
            try {
              l.mappedData = JSON.parse(l.data);
              l.isJson = true;
            } catch (e) {
              l.mappedData = l.data;
            }
          }
        });
      }
      const paymentStatus = checkPaymentStatus(d);
      const defaultJsonOptions = {
        mode: "view",
        editable: false,
      };
      const paymentLogs = _.map(d.paymentlogs, (l) => _.extend({}, l));
      setHistory({
        id: tx,
        jsonOptions: _.extend(
          { search: true, title: "Invoice" },
          defaultJsonOptions
        ),
        basicJsonOptions: _.extend(
          { search: false, title: "Payment Log" },
          defaultJsonOptions
        ),
        payment: {
          payment: d.payment,
          status: paymentStatus,
          logs: paymentLogs || [],
        },
        log: getTransactionLog(d),
        logStats: getLogStats(paymentLogs),
      });
    } else {
      toast.error(res?.message || "Failed to get transaction info");
    }
    setLoading(null);
  };

  const updateArtists = (sl, employees) => {
    sl.artists = _.compact(
      _.map(
        _.filter(
          employees,
          (e) => sl.employees && _.includes(sl.employees, e.id)
        ),
        (e) => e.nickname || e.full_name
      )
    ).join(", ");
  };

  const continueAfterCustomer = (selectedTransaction) => {
    const randstr =
      "abcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyz";
    const th = selectedBusinessDetail.info.loyaltyPointsThreshold;
    const thstr = randstr.substr(0, th).split("");
    const stars = [];
    if (!selectedBusiness.info.pointsPerDollar) {
      _.each(thstr, (l, i) => {
        stars.push({
          id: i,
          selected: false,
        });
      });
    }
    selectedTransaction.moreStars = stars;
    selectedTransaction.pointsPercent =
      (100 * selectedTransaction.currentPoints) / (th || 1);
  };

  const getCustomer = async (selectedTransaction) => {
    setLoading(LOADING_TYPES.FETCH_LOYALTY);
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/loyalty/detail?id=${selectedTransaction.subscriber_id}`
    );
    if (res.status) {
      if (res.data >= 0) {
        setError(null);
        selectedTransaction.currentPoints = res.data;
        continueAfterCustomer(selectedTransaction);
        setLoading(null);
        return selectedTransaction;
      }
      setLoading(null);
      setError("Request could not be fulfilled at this time.");
      return selectedTransaction;
    }
    continueAfterCustomer(selectedTransaction);
    setLoading(null);
    toast.error("Error occured. Please try again later");
    return selectedTransaction;

    // selectedTransaction.isLoading = true
  };

  const preProcessOnEdit = async (tx, employees) => {
    const selectedTransaction = { ...tx };
    selectedTransaction.staffList = [];

    if (selectedTransaction.checkout) {
      selectedTransaction.totalAmount =
        (selectedTransaction.checkout.cashamount || 0) +
        (selectedTransaction.checkout.creditamount || 0) +
        (selectedTransaction.checkout.giftamount || 0);
      _.each(selectedTransaction.checkout.saleslogs, (s) =>
        updateArtists(s, employees)
      );
      selectedTransaction.checkout.checkout_day_dt =
        selectedTransaction.checkout.checkout_day; // moment(, "YYYY-MM-DD").format("YYYY-MM-DD");

      if (selectedTransaction.currentPoints >= 0) {
        continueAfterCustomer(selectedTransaction);
        return selectedTransaction;
      }
      return getCustomer(selectedTransaction);
    }
    return tx;
  };

  const UPDATE_TXN = {
    sendBackToQueue: async (queueId) =>
      callApi(
        `/protected/business/${selectedBusiness.id}/queue/${queueId}/reactivate`,
        "POST",
        {
          src: "Uzeli Dashboard",
        }
      ),
    update: async (id, body) =>
      callApi(
        `/protected/business/${selectedBusiness.id}/updatetransaction/${id}`,
        "POST",
        body
      ),
  };

  const updateTransactionData = async (id, body) =>
    // {
    //   console.log(body);
    //   return { status: true };
    // };
    callApi(
      `/protected/business/${selectedBusiness.id}/iorequest?dest=:BID:/transaction/${id}/update`,
      "POST",
      body
    );

  const refundTransaction = async (queueId, payload) => {
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/dashboardrefund/${queueId}`,
      "POST",
      payload
    );
    return res;
  };

  // const markAsRefund = async (id) =>
  //   // console.log(body);
  //   // return { status: true };
  //   callApi(
  //     `/protected/business/${selectedBusiness.id}/iorequest?dest=:BID:/transaction/${id}/refund`,
  //     "POST"
  //   );
  return {
    getSummary,
    summary,
    loadingSummary: loading === LOADING_TYPES.FETCH_SUMMARY,
    getTransactions,
    transactions,
    byPassTransactions,
    loadingTxn: loading === LOADING_TYPES.FETCH_TRANSACTIONS,
    getDetail,
    deleteTransaction,
    loadingDelete: loading === LOADING_TYPES.DELETE,
    getHistory,
    loadingHistory: loading === LOADING_TYPES.FETCH_HISTORY,
    history,
    preProcessOnEdit,
    loadingLoyaltyFetch: loading === LOADING_TYPES.FETCH_LOYALTY,
    loyaltyFetchError: error,
    UPDATE_TXN,
    PRETTY_STATUS,
    updateTransactionData,
    refundTransaction,
  };
};

export default useDailyReports;
